<template>
  <div class="image-viewer"
       :class="{'image-viewer--empty' : !hasImages}"
       @click.self="onClickOut()">
    <div
      class="image-viewer__image-wrapper image-viewer__feature"
      @click.self="onClickOut()">
      <!-- <transition
        class="image-viewer__fade"
        name="fade"> -->
      <!-- <picture class="image-viewer__picture"> -->
      <img
        class="image-viewer__image image-viewer__feature-image"
        :class="{
          'image-viewer__feature-image--interactive' : interactive
        }"
        :src="`${activeImage.uri}?height=800&upscale=false`"
        :alt="activeImage.altText"
        @click="onClickIn()">
        <!-- </picture> -->
      <!-- </transition> -->
    </div>
    <div v-if="images.length > 1"
         class="image-viewer__previews">
      <div
        v-for="( image, index ) in filteredImages"
        :key="index"
        class="image-viewer__image-wrapper image-viewer__preview"
        :class="{
          'image-viewer__preview--active' : image.id === internalCurrentImageId
        }"
        @mouseenter="hoverPreview( image.id )"
        @mouseleave="clearHoveredPreview()">
        <!-- <picture> -->
        <img
          :src="`${image.uri}?height=200&upscale=false`"
          :alt="image.altText"
          class="image-viewer__image image-viewer__preview-image"
          @click="changeImage( image.id )">
        <!-- </picture> -->
      </div>
    </div>
  </div>
</template>

<script>
export default {
  /**
   * @typedef {object} Image An image
   * @property {number} id The unique ID of the image
   * @property {string} uri The src of the image file
   * @property {string} altText The alt-text of the image
   */

  model: {
    prop: "currentImageId",
    event: "change"
  },
  props: {
    /**
     * The images to display
     * @type {Image[]}
     * */
    images: {
      type: Array,
      default: () => []
    },
    /** The first image to load */
    currentImageId: {
      type: Number,
      default: null
    },
    /** Whether clicking the main image emits an event */
    interactive: {
      type: Boolean,
      default: false
    },
    /** This image displays if the source is blank */
    placeholder: {
      type: String,
      default: ""
    },
    /** Media height ranges to look out for
     *  @type {Number[]}
     */
    mediaHeightRanges: {
      type: Array,
      default: () => [600, 1200, 1600, 2000]
    }
  },
  data () {
    return {
      internalCurrentImageId: null,
      hoveredImageId: null,
      enterPreviewTimeoutInstance: null,
      exitPreviewTimeoutInstance: null,
      hoverDelay: 300,
      tallImage: {
        id: 1,
        altText: "Tall Image",
        uri: "https://picsum.photos/400/2400"
      },
      wideImage: {
        id: 2,
        altText: "Wide Image",
        uri: "https://picsum.photos/2400/400"
      }
    };
  },
  computed: {
    filteredImages () {
      let result = [];

      if ( this.images && this.images.length > 0 ) {
        result = this.images.filter( i => i.type !== "THUMBNAIL" );
      }

      return result;
    },
    activeImage () {
      let image = {
        id: -1,
        altText: "Placeholder Image",
        uri: this.placeholder
      };

      if ( this.hasImages ) {
        const imageId = this.hoveredImageId != null
            && this.hoveredImageId >= 0
          ? this.hoveredImageId
          : this.currentImageId;

        image = this.images.find( i => i.id === imageId ) ?? this.images[0];
      }

      return image;
    },
    hasImages () {
      let result = false;

      if ( this.images && this.images.length ) {
        result = true;
      }

      return result;
    }
  },
  watch: {
    currentImageId: {
      immediate: true,
      handler ( id ) {
        if ( this.internalCurrentImageId !== id ) {
          this.internalCurrentImageId = id;
        }
      }
    }
  },
  created () {
    this.internalCurrentImageId = this.currentImageId === null
      ? this.activeImage.id
      : this.currentImageId;

    this.changeImage( this.activeImage.id );
  },
  methods: {
    findImageIndex ( id ) {
      let index = -1;
      if ( this.hasImages && id !== null ) {
        index = this.images.findIndex(
          i => i.id === id
        );
      }
      return index;
    },
    /**
     * Change currentImageId to the supplied image
     * @param {number} id The id of the image to change to
     */
    changeImage ( id ) {
      if ( this.internalCurrentImageId !== id ) {
        this.$emit( "change", id );
        this.internalCurrentImageId = id;
        this.hoveredImageId = null;
        this.clearTimeouts();
      }
    },
    /**
     * Sets hoveredImageId to the supplied image
     * @param {number} id The id of the image
     */
    hoverPreview ( id ) {
      this.clearTimeouts();

      this.enterPreviewTimeoutInstance = window.setTimeout(
        () => {
          this.hoveredImageId = id;
        }, this.hoverDelay );
    },
    /**
     * Clears hoveredImageId to null
     * @param {number} id the id of the preview
     * to clear
     */
    clearHoveredPreview () {
      this.clearTimeouts();

      this.exitPreviewTimeoutInstance = window.setTimeout(
        () => {
          this.hoveredImageId = null;
        }, this.hoverDelay );
    },
    /** Clears all hover image timeouts */
    clearTimeouts () {
      window.clearTimeout( this.exitPreviewTimeoutInstance );
      window.clearTimeout( this.enterPreviewTimeoutInstance );
    },
    /**
     * Announces that the featured image has been
     * clicked (if it's enabled)
     */
    onClickIn () {
      if ( this.interactive ) {
        this.$emit( "click-in" );
      }
    },
    /**
     * Announces that the area outside of the featured
     * image has been clicked
     */
    onClickOut () {
      this.$emit( "click-out" );
    },
    /**
     * Log values at specific points at runtime
     * @param {string} name name of the call instance
     * @param {object} params any passed in parameters
     */
    logState ( name, params ) {
      const log = {
        interactive: this.interactive,
        caller: name,
        params: { ...params },
        currentImageId: this.currentImageId,
        internalCurrentImageId: this.internalCurrentImageId,
        hoveredImageId: this.hoveredImageId,
        activeImageId: this.activeImage.id
      };

      console.log( log );
    }
  }
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

// &--transition {
//   position: absolute;
// }
.image-viewer {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;

  &__image-wrapper {
    display: flex;
    justify-content: center;
    flex-grow: 1;
  }

  &__image {
    object-fit: contain;
    max-height: 100%;
    min-width: 0;
    min-height: 0;
  }

  &__feature {
    min-height: 225px;
  }

  &__feature-image {
    border: none;

    &--interactive {
      cursor: zoom-in;
    }
  }

  // &__picture {
  //   border: 2px solid hotpink;
  //   background: darken(hotpink, 20%);
  //   width: 100%;
  //   height: 100%;
  //   display: flex;
  //   // min-width: 0;
  // }

  &__previews {
    border: none;
    margin-top: 15px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    height: 100px;
    transition: height 0.2s;
    overflow-x: auto;

    @media screen and ( max-height: 600px ) {
      height: 70px;
    }
  }

  &__preview {
    height: 100%;
    min-width: 100px;
    padding: 4px;
    margin-right: 15px;
    flex: 0 1 auto;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border: 4px solid transparent;
    transition:
      border-color 0.2s,
      border-width 0.2s,
      padding 0.2s,
      margin-right 0.2s,
      background-color 0.2s;

    @media screen and ( max-height: 600px ), ( max-width: 800px ) {
      padding: 3px;
      margin-right: 5px;
      border-width: 3px;
      min-width: 60px;
    }

    &:last-child {
      margin-right: 0;
    }

    &:hover {
      border-color: @color-accent-8-lighter;
      background-color: rgba( 255, 255, 255, 0.4 );
    }

    &--active {
      border-color: @color-accent-6-light;
      background-color: rgba( 255, 255, 255, 0.4 );
    }
  }

  &__preview-image {
    border: none;
    max-height: 100%;
    width: auto;
    min-width: 0;
    min-height: 0;
  }

  .fade-enter-active {
    transition: opacity 0.5s ease-out;
  }

  .fade-leave-active {
    transition: opacity 0.5s ease-in;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
.image-viewer__test {
  @color-1: #8b0000;
  @color-2: #8f620e;
  @color-3: #a8962c;
  @color-4: #295329;
  @color-5: #31315c;
  @color-6: #4b0082;
  @color-7: #9c659c;
  @lighten: 50%;

  border: 2px solid @color-1;
  background-color: lighten( @color-1, @lighten );

  .image-viewer__image-wrapper {
    border: 2px solid @color-2;
    background-color: lighten( @color-2, @lighten );
  }

  .image-viewer__feature {
    border: 2px solid @color-3;
    background-color: lighten( @color-3, @lighten );
  }

  .image-viewer__feature-image {
    border: 2px solid @color-4;
    background-color: lighten( @color-4, @lighten );
  }

  .image-viewer__previews {
    border: 2px solid @color-5;
    background-color: lighten( @color-5, @lighten );
  }

  .image-viewer__preview {
    border: 2px solid @color-6;
    background-color: lighten( @color-6, @lighten );
  }

  .image-viewer__preview-image {
    border: 2px solid @color-7;
    background-color: lighten( @color-7, @lighten );
  }
}
</style>
